import * as React from "react";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { chartSelectStyles } from "components/charts/MainChart";
import {
  BarChartType, BoxPlotType, HeatMapType,
  AssessmentStageType, FirmOptionType, FitToOptionType, IntensityMetricType, ProjectListType,
  isBarChartType, isBoxPlotType, isHeatMapType, isPerformanceOverTimeType,
  ASSESSMENT_STAGES, FIT_TO_OPTIONS, INTENSITY_METRICS, PROJECT_LISTS,
  BOX_PLOTS, BAR_CHARTS, HEAT_MAPS, PERFORMANCE_OVER_TIME_PLOTS
} from "components/charts/types";
import { ReactComponent as CaretCircleDown } from '../../assets/icons/CaretCircleDown.svg'
import YearRangePicker, { YearRange } from "components/YearRangePicker/YearRangePicker";

interface IPropGroupedSelect {
  chartType: BoxPlotType | HeatMapType | BarChartType;
  setChartType: any;
  assessmentStage: AssessmentStageType;
  setAssessmentStage: any;
  intensityMetric: IntensityMetricType;
  setIntensityMetric: any;
  projectListType: ProjectListType;
  setProjectListType: any;
  fitToOption: FitToOptionType,
  setFitToOption: any,
  yearRange: YearRange;
  setYearRange: any;
  firmHighlighted: string,
  setFirmHighlighted: any,
  firmOptions: FirmOptionType[],
  siteRole: string
}

const GroupedSelect: React.FC<IPropGroupedSelect> = ({
  chartType,
  setChartType,
  assessmentStage,
  setAssessmentStage,
  intensityMetric,
  setIntensityMetric,
  projectListType,
  setProjectListType,
  fitToOption,
  setFitToOption,
  yearRange,
  setYearRange,
  firmHighlighted,
  setFirmHighlighted,
  firmOptions,
  siteRole
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    setChartType(event.target.value);
  };

  const handleAssessmentStageChange = (event: SelectChangeEvent) => {
    setAssessmentStage(event.target.value);
  };

  const handleIntensityMetricChange = (event: SelectChangeEvent) => {
    setIntensityMetric(event.target.value);
  };

  const handleProjectListTypeChange = (event: SelectChangeEvent) => {
    setProjectListType(event.target.value);
  };

  const handleOutlierDisplayTypeChange = (event: SelectChangeEvent) => {
    setFitToOption(event.target.value);
  };

  const handleFirmHighlightedChange = (event: SelectChangeEvent) => {
    setFirmHighlighted(event.target.value);
  };

  const renderVisualizationSelect = () => {
    return (
      <FormControl sx={{ m: 1, width: '100%', alignItems: 'center' }}>
        <InputLabel sx={{ position: 'relative', width: 350, top: '8px' }} htmlFor="grouped-native-select">
          Visualization
        </InputLabel>
        <Select
          value={chartType}
          defaultValue="Primary Building Use Type"
          onChange={handleChange}
          id="grouped-select"
          sx={{ ...chartSelectStyles, width: 350, marginTop: 0, marginBottom: 2 }}
          IconComponent={CaretCircleDown}
        >
          <ListSubheader color="primary">Distribution</ListSubheader>
          {BOX_PLOTS.filter(bP => !isPerformanceOverTimeType(bP)).map(bP => <MenuItem key={bP} value={bP}>{bP}</MenuItem>)}
          <ListSubheader color="primary">Breakdown</ListSubheader>
          {BAR_CHARTS.map(bC => <MenuItem key={bC} value={bC}>{bC}</MenuItem>)}
          <ListSubheader color="primary">Heatmap</ListSubheader>
          {HEAT_MAPS.map(hM => <MenuItem key={hM} value={hM}>{hM}</MenuItem>)}
          <ListSubheader color="primary">Performance Over Time</ListSubheader>
          {PERFORMANCE_OVER_TIME_PLOTS.map(pOTP => <MenuItem key={pOTP} value={pOTP}>{pOTP}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }

  const renderProjectListSelect = () => {
    if ([ 'Firm User', 'Firm Admin' ].includes(siteRole) && (isHeatMapType(chartType) || isPerformanceOverTimeType(chartType))) {
      return (
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel htmlFor="grouped-native-select">
            Project List
          </InputLabel>
          <Select
            value={projectListType}
            defaultValue="All Projects"
            onChange={handleProjectListTypeChange}
            id="grouped-select"
            sx={chartSelectStyles}
            IconComponent={CaretCircleDown}
          >
            {PROJECT_LISTS.map(pL => <MenuItem key={pL} value={pL}>{pL}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
  }

  const renderOutlierDisplaySelect = () => {
    if (siteRole !== 'Sub Super Admin' && isBoxPlotType(chartType) && !isPerformanceOverTimeType(chartType)) {
      return (
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel htmlFor="grouped-native-select">
            X-Axis Bounds
          </InputLabel>
          <Select
            value={fitToOption}
            defaultValue="Zoomed In to Core Distribution"
            onChange={handleOutlierDisplayTypeChange}
            id="grouped-select"
            sx={chartSelectStyles}
            IconComponent={CaretCircleDown}
          >
            {FIT_TO_OPTIONS.map(fT => <MenuItem key={fT} value={fT}>{fT}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
  }

  const renderAssessmentStageSelect = () => {
    if (!isHeatMapType(chartType) && !isPerformanceOverTimeType(chartType) && !isBarChartType(chartType)) {
      return (
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel htmlFor="grouped-native-select">
            Assessment Stage
          </InputLabel>
          <Select
            value={assessmentStage}
            defaultValue="A1-A3"
            onChange={handleAssessmentStageChange}
            id="grouped-select"
            sx={chartSelectStyles}
            IconComponent={CaretCircleDown}
          >
            {ASSESSMENT_STAGES.map(aS => <MenuItem key={aS} value={aS}>{aS}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
  }

  const renderIntensityMetricSelect = () => {
    if ([ 'Concrete Use Intensity by Element',
          'Steel Use Intensity by Element',
          'Timber Use Intensity by Element' ].includes(chartType)) {
      return (
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel htmlFor="grouped-native-select">
            Intensity Metric
          </InputLabel>
          <Select
            value={intensityMetric}
            defaultValue="Use"
            onChange={handleIntensityMetricChange}
            id="grouped-select"
            sx={chartSelectStyles}
            IconComponent={CaretCircleDown}
          >
            {INTENSITY_METRICS.map(iM => <MenuItem key={iM} value={iM}>{iM}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
  }

  const renderYearRangePicker = () => {
    if (!isPerformanceOverTimeType(chartType)) {
      return (
        <YearRangePicker
          yearRange={yearRange}
          setYearRange={setYearRange}
        />
      );
    }
  }

  const renderFirmSelect = () => {
    if (siteRole === 'Super Admin') {
      return (
        <FormControl sx={{ m: 1, width: 300 }}>
          <InputLabel htmlFor="grouped-native-select" shrink>Firm</InputLabel>
          <Select
            value={firmHighlighted}
            defaultValue=""
            displayEmpty
            onChange={handleFirmHighlightedChange}
            id="grouped-select"
            sx={chartSelectStyles}
            IconComponent={CaretCircleDown}
          >
            {firmOptions.map((firm: FirmOptionType, ind: number) => (
              <MenuItem value={firm.value} key={ind}>
                {firm.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
  }

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      columnGap: '16px',
      rowGap: 0
    }}>
      {renderVisualizationSelect()}
      {renderProjectListSelect()}
      {renderAssessmentStageSelect()}
      {renderIntensityMetricSelect()}
      {renderYearRangePicker()}
      {renderFirmSelect()}
      {renderOutlierDisplaySelect()}
    </div>
  );
};

export default GroupedSelect;